import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { parseApiError } from "utils/parseApiError";
import { UNKNOWN_FLOOR_PLANS_ERROR_CODE } from "../constants";
import type { FloorPlanFolderImportResult } from "../../utils/types";
import type {
  ImportFloorPlanFoldersVariables,
  UseImportFloorPlanFolders,
} from "./types";

export const useImportFloorPlanFolders: UseImportFloorPlanFolders = ({
  projectId,
  onFoldersImported,
}) => {
  const { t } = useTranslation();

  const importMutation = useMutation({
    mutationFn: ({
      folders,
      overwrite,
    }: ImportFloorPlanFoldersVariables): Promise<FloorPlanFolderImportResult> =>
      api.importQuantitySurveyorFloorPlanFolders({
        projectId,
        overwrite,
        folders,
      }),
    onSuccess: (result, { structure, overwrite }) => {
      onFoldersImported(result);
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_FOLDER_SUGGESTION_APPLIED, {
        structure,
        folderCount: result.folders.length,
        plansMoved: result.plansMoved,
        overwrite,
      });
      toast.success(
        t("smartSurveyor.folders.suggestion.applied", {
          count: result.plansMoved,
        }),
      );
    },
    onError: (error) => {
      const arePlansGone =
        parseApiError({ error }).errorCode === UNKNOWN_FLOOR_PLANS_ERROR_CODE;
      toast.error(
        t(
          arePlansGone
            ? "smartSurveyor.folders.suggestion.plansGone"
            : "smartSurveyor.folders.suggestion.applyFailed",
        ),
      );
    },
  });

  return {
    isImporting: importMutation.isPending,
    importFolders: importMutation.mutateAsync,
  };
};
